import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { InterviewStatus, CreateInterviewResponse } from '@recruitai/shared';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCandidate } from '@/hooks/useCandidate';
import { useCreateInterview } from '@/hooks/useCreateInterview';
import { useInterview } from '@/hooks/useInterview';
import { useInterviewStatus } from '@/hooks/useInterviewStatus';
import { useInterviews } from '@/hooks/useInterviews';
import { QuestionListEditor } from '@/components/interviews/QuestionListEditor';
import { TranscriptUploadZone } from '@/components/interviews/TranscriptUploadZone';
import { AudioUploadZone } from '@/components/interviews/AudioUploadZone';
import { InterviewStatusTracker } from '@/components/interviews/InterviewStatusTracker';
import { ScoreReport } from '@/components/interviews/ScoreReport';
import { useAudioUpload } from '@/hooks/useAudioUpload';
import { apiClient } from '@/lib/api-client';
import { toast } from 'sonner';

type Phase = 'questions' | 'upload' | 'processing' | 'result';
type InputTab = 'file' | 'paste' | 'audio';

export function InterviewPage() {
  const { jobId, candidateId } = useParams<{ jobId: string; candidateId: string }>();
  const navigate = useNavigate();
  const { candidate, isLoading: candidateLoading } = useCandidate(jobId!, candidateId!);
  const { interviews } = useInterviews(jobId!, candidateId!);
  const { createInterview, isCreating } = useCreateInterview();

  const [phase, setPhase] = useState<Phase>('questions');
  const [currentInterview, setCurrentInterview] = useState<CreateInterviewResponse | null>(null);
  const [inputTab, setInputTab] = useState<InputTab>('file');
  const [pasteText, setPasteText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [extractedFileName, setExtractedFileName] = useState('');

  const interviewId = currentInterview?.interviewId || '';
  const { interview, refetch: refetchInterview } = useInterview(jobId!, candidateId!, interviewId);
  const { uploadAudio, isUploading, progress } = useAudioUpload(jobId!, candidateId!, interviewId);
  const { status } = useInterviewStatus(jobId!, candidateId!, interviewId, phase === 'processing');

  // Quando status muda para done durante processing
  const [doneHandled, setDoneHandled] = useState(false);
  
  if (phase === 'processing' && status === InterviewStatus.DONE && !doneHandled) {
    setDoneHandled(true);
    refetchInterview().then(() => {
      setPhase('result');
      toast.success('Análise da entrevista concluída!');
    });
  }

  const handleProceedToUpload = async () => {
    try {
      const result = await createInterview(jobId!, candidateId!, 'text');
      setCurrentInterview(result);
      setPhase('upload');
    } catch {
      toast.error('Erro ao iniciar entrevista');
    }
  };

  const handleTextExtracted = (text: string, fileName: string) => {
    setExtractedText(text);
    setExtractedFileName(fileName);
  };

  const handleSubmitText = async (rawInput: string) => {
    if (!rawInput.trim()) {
      toast.error('Nenhum conteúdo para enviar.');
      return;
    }
    setIsSubmitting(true);
    try {
      await apiClient.post(
        `/jobs/${jobId}/candidates/${candidateId}/interviews/${interviewId}/finalize`,
        { rawInput }
      );
      toast.success('Conteúdo enviado. Analisando...');
      setPhase('processing');
    } catch {
      toast.error('Erro ao enviar conteúdo');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAudioFile = async (file: File) => {
    try {
      await uploadAudio(file);
      setPhase('processing');
    } catch {
      // Erro tratado no hook
    }
  };

  if (candidateLoading) {
    return <div className="p-8 text-center text-muted-foreground">Carregando...</div>;
  }

  if (!candidate) {
    return <div className="p-8 text-center text-red-600">Candidato não encontrado</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Entrevista</h1>
          <p className="text-sm text-muted-foreground">{candidate.name} • {candidate.email}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>Voltar</Button>
      </div>

      {/* Entrevistas anteriores */}
      {interviews.length > 0 && phase === 'questions' && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-2">Entrevistas Anteriores ({interviews.length})</h3>
          <div className="space-y-1">
            {interviews.map((iv) => (
              <div key={iv.interviewId} className="flex items-center justify-between text-sm cursor-pointer hover:bg-gray-50 p-1 rounded"
                onClick={() => navigate(`/jobs/${jobId}/candidates/${candidateId}/interviews/${iv.interviewId}`)}>
                <span>📋 {new Date(iv.conductedAt).toLocaleDateString('pt-BR')}</span>
                <span className={iv.status === 'done' ? 'text-green-600' : 'text-yellow-600'}>
                  {iv.status === 'done' ? `Score: ${iv.interviewScore}` : iv.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Fase: Perguntas */}
      {phase === 'questions' && (
        <>
          <QuestionListEditor
            jobId={jobId!}
            candidateId={candidateId!}
            initialQuestions={candidate.interviewQuestions}
          />
          <div className="flex justify-end">
            <Button onClick={handleProceedToUpload} disabled={isCreating}>
              {isCreating ? 'Criando...' : 'Enviar Transcrição da Entrevista →'}
            </Button>
          </div>
        </>
      )}

      {/* Fase: Upload de transcrição */}
      {phase === 'upload' && currentInterview && (
        <Card className="p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Enviar Transcrição da Entrevista</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Suba o arquivo de transcrição ou resumo gerado pelo Zoom, Meet ou Teams.
              A IA vai extrair as respostas e analisar o candidato.
            </p>
          </div>

          <Tabs value={inputTab} onValueChange={(v) => setInputTab(v as InputTab)}>
            <TabsList>
              <TabsTrigger value="file">📄 Arquivo (PDF/TXT/DOCX)</TabsTrigger>
              <TabsTrigger value="paste">📋 Colar Texto</TabsTrigger>
              <TabsTrigger value="audio">🎙️ Áudio</TabsTrigger>
            </TabsList>

            <TabsContent value="file" className="space-y-3 mt-4">
              <TranscriptUploadZone
                onTextExtracted={handleTextExtracted}
                isProcessing={isSubmitting}
              />
              {extractedText && (
                <div className="space-y-2">
                  <p className="text-sm text-green-600 font-medium">
                    ✅ Texto extraído de {extractedFileName} ({extractedText.length.toLocaleString()} caracteres)
                  </p>
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Pré-visualizar texto extraído</summary>
                    <pre className="mt-2 p-3 bg-gray-50 rounded text-xs max-h-48 overflow-auto whitespace-pre-wrap">
                      {extractedText.slice(0, 2000)}{extractedText.length > 2000 ? '...' : ''}
                    </pre>
                  </details>
                  <div className="flex justify-end">
                    <Button onClick={() => handleSubmitText(extractedText)} disabled={isSubmitting}>
                      {isSubmitting ? 'Enviando...' : 'Analisar Entrevista'}
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="paste" className="space-y-3 mt-4">
              <Textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Cole aqui o texto da transcrição ou resumo da entrevista..."
                rows={10}
              />
              <div className="flex justify-end">
                <Button
                  onClick={() => handleSubmitText(pasteText)}
                  disabled={!pasteText.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Analisar Entrevista'}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="audio" className="mt-4">
              <AudioUploadZone
                onFileSelected={handleAudioFile}
                isUploading={isUploading}
                progress={progress}
              />
            </TabsContent>
          </Tabs>
        </Card>
      )}

      {/* Fase: Processamento */}
      {phase === 'processing' && currentInterview && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Analisando Entrevista</h3>
          <InterviewStatusTracker status={status} mode={currentInterview.mode} />
          <p className="text-sm text-muted-foreground text-center mt-4">
            A IA está extraindo respostas e avaliando o candidato...
          </p>
        </Card>
      )}

      {/* Fase: Resultado */}
      {phase === 'result' && interview && interview.interviewFeedback && (
        <ScoreReport
          feedback={interview.interviewFeedback}
          finalScore={interview.finalScore}
          fitScore={candidate.fitScore || 0}
          candidateName={candidate.name}
          candidateEmail={candidate.email}
          interviewDate={interview.conductedAt}
        />
      )}
    </div>
  );
}
